/**
 * Manual fixed-position tooltip placement for AG Grid cells.
 * Popper/transform-based positioning breaks inside AG Grid center viewports
 * (row/column transforms + horizontal scroll), so we use viewport rects directly.
 *
 * @param {HTMLElement} reference
 * @param {HTMLElement} tooltip
 * @param {{ placement?: string, offset?: number }} [options]
 */
export function showDataGridTooltip(
    reference,
    tooltip,
    { placement = 'top-start', offset = 8 } = {},
) {
    tooltip.style.display = 'block';
    tooltip.style.position = 'fixed';
    tooltip.style.zIndex = '10000';
    tooltip.style.margin = '0';
    tooltip.style.transform = 'none';
    tooltip.style.visibility = 'hidden';
    tooltip.style.left = '0';
    tooltip.style.top = '0';

    const refRect = reference.getBoundingClientRect();
    const { width: tipWidth, height: tipHeight } = tooltip.getBoundingClientRect();

    let top;
    let left;

    if (placement === 'top-end') {
        top = refRect.top - tipHeight - offset;
        left = refRect.right - tipWidth;
    } else if (placement === 'top-start') {
        top = refRect.top - tipHeight - offset;
        left = refRect.left;
    } else if (placement === 'bottom') {
        top = refRect.bottom + offset;
        left = refRect.left + (refRect.width - tipWidth) / 2;
    } else if (placement === 'bottom-start') {
        top = refRect.bottom + offset;
        left = refRect.left;
    } else if (placement === 'bottom-end') {
        top = refRect.bottom + offset;
        left = refRect.right - tipWidth;
    } else {
        top = refRect.top - tipHeight - offset;
        left = refRect.left + (refRect.width - tipWidth) / 2;
    }

    const padding = 8;
    left = Math.max(padding, Math.min(left, window.innerWidth - tipWidth - padding));
    top = Math.max(padding, Math.min(top, window.innerHeight - tipHeight - padding));

    tooltip.style.left = `${Math.round(left)}px`;
    tooltip.style.top = `${Math.round(top)}px`;
    tooltip.style.visibility = 'visible';
    tooltip.setAttribute('data-popper-placement', placement);
}

/**
 * @param {HTMLElement} tooltip
 */
export function hideDataGridTooltip(tooltip) {
    tooltip.style.display = 'none';
    tooltip.style.visibility = '';
    tooltip.style.left = '';
    tooltip.style.top = '';
    tooltip.style.transform = '';
    tooltip.removeAttribute('data-popper-placement');
}

// Backwards-compatible names used by callers that tracked a Popper instance.
export const createTooltipPopper = showDataGridTooltip;
export const destroyTooltipPopper = (tooltip) => hideDataGridTooltip(tooltip);
